import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { WebPageSchema } from '@/components/base/SchemaOrg';
import { videos, featuredCreators } from '@/mocks/videos';
import { categories } from '@/mocks/categories';

export default function Home() {
  const hotVideos = videos.filter(v => v.isHot).slice(0, 8);
  const newVideos = videos.filter(v => v.isNew).slice(0, 4);
  const travelVideos = videos.filter(v => v.category === 'travel').slice(0, 4);
  const foodVideos = videos.filter(v => v.category === 'food').slice(0, 4);

  return (
    <>
      <ScrollToTop />
      <title>海角社区视频 - 发现精彩内容，畅享社区视频乐趣</title>
      <meta name="description" content="海角社区视频汇聚海量优质内容，用户可在海角社区视频平台自由分享、浏览、互动，发现最新最热的社区视频资源，加入活跃创作者社群，体验真实、有趣、多元的视频社区文化。" />
      <meta name="keywords" content="海角社区视频,海角社区视频在线观看,海角社区视频分享,海角社区视频最新内容,海角社区视频创作者,社区视频平台推荐" />
      <link rel="canonical" href="https://haijiao.shipin.com" />
      <meta property="og:title" content="海角社区视频 - 发现精彩内容，畅享社区视频乐趣" />
      <meta property="og:description" content="海角社区视频汇聚海量优质内容，用户可在海角社区视频平台自由分享、浏览、互动，发现最新最热的社区视频资源。" />
      <meta property="og:url" content="https://haijiao.shipin.com" />
      <meta property="og:type" content="website" />
      <WebPageSchema
        title="海角社区视频 - 发现精彩内容，畅享社区视频乐趣"
        description="海角社区视频汇聚海量优质内容，用户可在海角社区视频平台自由分享、浏览、互动，发现最新最热的社区视频资源。"
        url="https://haijiao.shipin.com"
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full min-h-[620px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Vibrant%20modern%20creative%20community%20space%20with%20warm%20coral%20and%20amber%20toned%20abstract%20geometric%20shapes%2C%20flowing%20gradient%20waves%2C%20energetic%20and%20dynamic%20composition%2C%20inspirational%20digital%20media%20background%2C%20soft%20layered%20textures%2C%20warm%20inviting%20atmosphere%2C%20modern%20creative%20workspace%20vibe&width=1920&height=900&seq=hero-bg-home&orientation=landscape"
            alt="海角社区视频平台"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-900/40 via-foreground-900/20 to-foreground-900/50"></div>

          <div className="relative z-10 w-full max-w-4xl mx-auto text-center px-4">
            <h1 className="text-3xl md:text-5xl font-bold text-background-50 mb-5 leading-tight">
              发现精彩内容
              <br />
              畅享<span className="text-primary-400">社区视频</span>乐趣
            </h1>
            <p className="text-base md:text-lg text-background-50/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              海角社区视频汇聚海量优质内容，自由分享、浏览、互动，发现最新最热的社区视频资源，加入活跃创作者社群
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-10">
              <Link
                to="/category/all"
                className="px-8 py-3.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap w-full sm:w-auto"
              >
                <i className="ri-play-circle-line mr-2"></i>
                开始探索
              </Link>
              <Link
                to="/about"
                className="px-8 py-3.5 rounded-full bg-background-50/15 text-background-50 font-medium text-sm border border-background-50/25 hover:bg-background-50/25 transition-all duration-300 whitespace-nowrap w-full sm:w-auto"
              >
                <i className="ri-information-line mr-2"></i>
                了解更多
              </Link>
            </div>

            <div className="flex items-center justify-center gap-8 text-background-50/70">
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-background-50">50K+</p>
                <p className="text-xs mt-1">活跃创作者</p>
              </div>
              <div className="w-px h-10 bg-background-50/20"></div>
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-background-50">800万+</p>
                <p className="text-xs mt-1">月活跃用户</p>
              </div>
              <div className="w-px h-10 bg-background-50/20"></div>
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-background-50">120亿+</p>
                <p className="text-xs mt-1">累计播放量</p>
              </div>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-background-50 to-transparent"></div>
        </section>

        {/* Category Navigation */}
        <section className="w-full bg-background-50 py-6">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={cat.id === 'all' ? '/category/all' : `/category/${cat.slug}`}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-background-100 border border-background-200 text-foreground-600 text-sm font-medium hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50/50 transition-all duration-200 whitespace-nowrap flex-shrink-0"
                >
                  <div className="w-6 h-6 rounded-md bg-accent-50 flex items-center justify-center flex-shrink-0">
                    <i className={`${cat.icon} text-accent-600 text-xs`}></i>
                  </div>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Videos Section */}
        <section className="w-full bg-background-50 py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-7">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 flex items-center gap-2">
                  <i className="ri-fire-line text-primary-500"></i>
                  热门推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">大家都在看的热门视频内容</p>
              </div>
              <Link to="/category/all" className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap flex items-center gap-1">
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {hotVideos.map((video) => (
                <VideoCard key={video.id} {...video} />
              ))}
            </div>
          </div>
        </section>

        {/* Travel & Food Combined */}
        <section className="w-full bg-background-100 py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              {/* Travel */}
              <div>
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-lg md:text-xl font-bold text-foreground-900 flex items-center gap-2">
                    <i className="ri-earth-line text-accent-500"></i>
                    旅行户外
                  </h2>
                  <Link to="/category/travel" className="text-sm text-accent-600 hover:text-accent-700 font-medium whitespace-nowrap">
                    更多 <i className="ri-arrow-right-line text-xs"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {travelVideos.map((video) => (
                    <VideoCard key={video.id} {...video} />
                  ))}
                </div>
              </div>

              {/* Food */}
              <div>
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-lg md:text-xl font-bold text-foreground-900 flex items-center gap-2">
                    <i className="ri-restaurant-line text-primary-500"></i>
                    美食烹饪
                  </h2>
                  <Link to="/category/food" className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap">
                    更多 <i className="ri-arrow-right-line text-xs"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {foodVideos.map((video) => (
                    <VideoCard key={video.id} {...video} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* New Videos */}
        <section className="w-full bg-background-50 py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-7">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 flex items-center gap-2">
                  <i className="ri-sparkling-line text-accent-500"></i>
                  最新上架
                </h2>
                <p className="text-sm text-foreground-500 mt-1">刚刚发布的新鲜视频内容</p>
              </div>
              <Link to="/category/all" className="text-sm text-accent-600 hover:text-accent-700 font-medium whitespace-nowrap flex items-center gap-1">
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {newVideos.map((video) => (
                <VideoCard key={video.id} {...video} />
              ))}
            </div>
          </div>
        </section>

        {/* Featured Creators */}
        <section className="w-full bg-background-100 py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900">
                海角社区视频 · 推荐创作者
              </h2>
              <p className="text-sm text-foreground-500 mt-2">加入活跃创作者社群，发现更多精彩</p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {featuredCreators.map((creator) => (
                <div key={creator.id} className="text-center group">
                  <div className="relative w-20 h-20 mx-auto mb-3 rounded-full overflow-hidden border-2 border-background-200 group-hover:border-primary-300 transition-all duration-300">
                    <img src={creator.avatar} alt={creator.name} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <p className="text-sm font-medium text-foreground-900">{creator.name}</p>
                  <p className="text-xs text-foreground-400 mt-0.5">{creator.followers} 粉丝</p>
                  <p className="text-xs text-foreground-500 mt-1.5 line-clamp-2 leading-relaxed">{creator.bio}</p>
                </div>
              ))}
            </div>

            <div className="text-center mt-8">
              <button className="px-8 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap">
                成为创作者
              </button>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="max-w-3xl mx-auto text-center px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
              加入海角社区视频
            </h2>
            <p className="text-foreground-500 mb-8 leading-relaxed max-w-xl mx-auto">
              无论你是想发现精彩内容，还是想分享自己的创作，海角社区视频都欢迎你的加入。一起畅享社区视频乐趣！
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/all"
                className="px-8 py-3.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap w-full sm:w-auto"
              >
                <i className="ri-play-circle-line mr-2"></i>
                立即浏览
              </Link>
              <Link
                to="/about"
                className="px-8 py-3.5 rounded-full bg-secondary-100 text-secondary-700 font-medium text-sm hover:bg-secondary-200 transition-colors whitespace-nowrap w-full sm:w-auto"
              >
                <i className="ri-information-line mr-2"></i>
                关于我们
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}